import axios from 'axios';
import { restUrl } from '../../env';
import { analytics } from './AnalyticsManager';

export let sessionUserID = undefined;
export let sessionUsername = undefined;
export let sessionUserKarmaScore = undefined;
export let sessionUserKarmaTotal = undefined;
export let sessionUsernameChangesRemaining = undefined;
export let sessionUserLoggedIn = undefined;

export let sessionUserTotalGamesPlayed = undefined;
export let sessionUserTotalGamesWon = undefined;
export let sessionUserTotalWinningPercentage = undefined;
export let sessionUserLast100GamesWon = undefined;
export let sessionUserLast100GamesCompleted = undefined;
export let sessionUserLast100PlayedGames = undefined;

let onLoggedInCallbacks = [];
let onUsernameChangedCallbacks = [];

export async function WaitUntilLoggedIn( loggedInCallback )
{
  if( sessionUsername )
  {
    // Already logged in! Callback immediately
    await loggedInCallback();
  }
  else
  {
    onLoggedInCallbacks.push( loggedInCallback );
  }
}

export function RegisterOnUsernameChangeCallback( onChangeCallback )
{
  if( onUsernameChangedCallbacks.includes( onChangeCallback ) )
  {
    // This callback method is already registered
    console.log( "RegisterOnUsernameChangeCallback() - This callback method is already registered" );
    return;
  }

  onUsernameChangedCallbacks.push( onChangeCallback );
}

export function UnregisterOnUsernameChangeCallback( onChangeCallback )
{
  let index = onUsernameChangedCallbacks.indexOf( onChangeCallback );

  if( index >= 0 )
  {
    onUsernameChangedCallbacks.splice( index, 1 );
  }
  else
  {
    console.log( "UnregisterOnUsernameChangeCallback() Couldn't find callback registered..." );
  }
}


async function CallOnLoggedInCallbacks()
{
  for( let idx = 0; idx < onLoggedInCallbacks.length; idx++ )
  {
    await onLoggedInCallbacks[ idx ]();
  }

  onLoggedInCallbacks = [];
}

function CallOnUsernameChangedCallbacks( newUsername )
{
  for( let idx = 0; idx < onUsernameChangedCallbacks.length; idx++ )
  {
    onUsernameChangedCallbacks[ idx ]( newUsername );
  }
}

export async function AttemptUserLoginFlow( loginCallback, onConnectionError )
{
  try
  {
    const response = await axios.post( `${restUrl}/auth/verify`, {}, { withCredentials: true } );

    await resolveLoginResponse( response, loginCallback );
  }
  catch( error )
  {
    // Handle connection errors (e.g., server down, network issues)
    if( error.code === 'ERR_NETWORK' || error.message?.includes( 'ERR_CONNECTION_REFUSED' ) || !error.response )
    {
      console.log( "Login flow failed due to connection error:", error.message );

      // Call the connection error callback if provided
      if( onConnectionError )
      {
        onConnectionError( {
          title: 'Connection Error',
          content: 'Cannot connect to the server. Please check your internet connection and try again.',
          isConnectionError: true
        } );
      }
      return;
    }

    // For other types of errors, re-throw them
    throw error;
  }
}


export async function LoginWithGoogle( googleData, loginCallback )
{
  if( !sessionUsername )
  {
    // Login with Google attempted before a non-social account login has completed
    console.log( "Login with Google attempted before a non-social account login has completed. Failing login" );
    return;
  }

  // NOTE: googleData.credential is the only important field returned. Other data is the google app 'clientId' (which we store in .env and passed in)

  const response = await axios.post( `${restUrl}/auth/googleLogin`, { idToken: googleData.credential }, { withCredentials: true } );

  await resolveLoginResponse( response, loginCallback );
}

export async function LogoutUser()
{
  if( sessionUserLoggedIn === undefined || !sessionUserLoggedIn )
  {
    console.log( 'Requested to logout a user that is not logged in.' );
    return;
  }

  console.log( "On User Logout" );

  await axios.post( `${restUrl}/auth/logout`, {}, { withCredentials: true } );

  sessionUserID = undefined;
  sessionUsername = undefined;
  sessionUserKarmaScore = undefined;
  sessionUserKarmaTotal = undefined;
  sessionUsernameChangesRemaining = undefined;
  sessionUserLoggedIn = undefined;
}

export async function ChangeUsername( newUsername )
{
  // console.log("ChangeUsername() newUsername", newUsername);

  const response = await axios.post( `${restUrl}/auth/changeUsername`, { newUsername: newUsername }, { withCredentials: true } );

  if( !response )
  {
    return { success: false, error: "Server error" };
  }

  if( !response.data.success )
  {
    return response.data;
  }

  const { username, tokenData, karmaScore, karmaTotal, usernameChangesLeft } = response.data;

  // console.log("Updated login username : ", username);

  sessionUsername = username;
  sessionUserKarmaScore = karmaScore;
  sessionUserKarmaTotal = karmaTotal;
  sessionUsernameChangesRemaining = usernameChangesLeft;

  CallOnUsernameChangedCallbacks( sessionUsername );

  return { success: true };
};


const resolveLoginResponse = async ( response, loginCallback ) =>
{
  if( !response || !response.data )
  {
    console.log( "No login response." );
    return;
  }

  if( response.data.success )
  {
    const verifiedResponse = response.data;

    sessionUserID = verifiedResponse.userID;
    sessionUsername = verifiedResponse.username;
    sessionUserKarmaScore = verifiedResponse.karmaScore;
    sessionUserKarmaTotal = verifiedResponse.karmaTotal;
    sessionUsernameChangesRemaining = verifiedResponse.usernameChangesLeft;
    sessionUserLoggedIn = verifiedResponse.isLoggedIn;

    sessionUserTotalGamesPlayed = verifiedResponse.totalGamesPlayed;
    sessionUserTotalGamesWon = verifiedResponse.totalGamesWon;
    sessionUserTotalWinningPercentage = verifiedResponse.totalWinningPercentage;
    sessionUserLast100GamesWon = verifiedResponse.last100GamesWon;
    sessionUserLast100GamesCompleted = verifiedResponse.last100GamesCompleted;
    sessionUserLast100PlayedGames = verifiedResponse.last100PlayedGames;

    analytics.setUserId( sessionUserID );

    analytics.setUserProperties(
      [
        { field: "signUpDate", value: new Date().toISOString(), setOnce: true },
        { field: "username", value: sessionUsername },
        { field: "isLoggedIn", value: sessionUserLoggedIn },

        { field: "totalGamesPlayed", value: sessionUserTotalGamesPlayed },
        { field: "totalGamesWon", value: sessionUserTotalGamesWon },
        { field: "totalWinningPercentage", value: sessionUserTotalWinningPercentage },
        { field: "last100GamesWon", value: sessionUserLast100GamesWon },
        { field: "last100GamesCompleted", value: sessionUserLast100GamesCompleted },
        { field: "last100PlayedGames", value: sessionUserLast100PlayedGames },

        { field: "karmaScore", value: sessionUserKarmaScore },
        { field: "karmaTotal", value: sessionUserKarmaTotal },
        { field: "usernameChangesLeft", value: sessionUsernameChangesRemaining },
      ] );

    loginCallback();

    await CallOnLoggedInCallbacks();

    return;
  }
  else
  {
    console.log( "Login Failed. Error: ", response.data.error );
  }
};






